import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const mealTypes = [
    'breakfast',
    'mid_morning',
    'lunch',
    'snack',
    'dinner',
    'supper',
    'other'
];

const OptionEditor = ({ dayId, mealId, option, optionIndex, dispatch }) => {
    const { t } = useTranslation();

    return (
        <Box sx={{ py: 2 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
            >
                <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                    {t('nutrition.editor.optionNumber', {
                        number: optionIndex + 1
                    })}
                </Typography>
                <Tooltip title={t('nutrition.editor.deleteOption')}>
                    <IconButton
                        color="error"
                        aria-label={t('nutrition.editor.deleteOption')}
                        onClick={() =>
                            dispatch({
                                type: 'deleteOption',
                                dayId,
                                mealId,
                                optionId: option._uiId
                            })
                        }
                    >
                        <DeleteOutlineIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Stack spacing={1.5} sx={{ mt: 2 }}>
                {option.items.map((item) => (
                    <Stack
                        key={item._uiId}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ sm: 'center' }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            label={t('nutrition.food')}
                            value={item.name}
                            onChange={(event) =>
                                dispatch({
                                    type: 'updateItem',
                                    dayId,
                                    mealId,
                                    optionId: option._uiId,
                                    itemId: item._uiId,
                                    field: 'name',
                                    value: event.target.value
                                })
                            }
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label={t('nutrition.quantity')}
                            value={item.quantity}
                            onChange={(event) =>
                                dispatch({
                                    type: 'updateItem',
                                    dayId,
                                    mealId,
                                    optionId: option._uiId,
                                    itemId: item._uiId,
                                    field: 'quantity',
                                    value: event.target.value
                                })
                            }
                        />
                        <Tooltip title={t('nutrition.editor.deleteFood')}>
                            <span>
                                <IconButton
                                    color="error"
                                    disabled={option.items.length === 1}
                                    aria-label={t('nutrition.editor.deleteFood')}
                                    onClick={() =>
                                        dispatch({
                                            type: 'deleteItem',
                                            dayId,
                                            mealId,
                                            optionId: option._uiId,
                                            itemId: item._uiId
                                        })
                                    }
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                ))}
            </Stack>

            <Button
                size="small"
                startIcon={<AddIcon />}
                sx={{ mt: 1 }}
                onClick={() =>
                    dispatch({
                        type: 'addItem',
                        dayId,
                        mealId,
                        optionId: option._uiId
                    })
                }
            >
                {t('nutrition.editor.addFood')}
            </Button>
        </Box>
    );
};

OptionEditor.propTypes = {
    dayId: PropTypes.string.isRequired,
    mealId: PropTypes.string.isRequired,
    option: PropTypes.object.isRequired,
    optionIndex: PropTypes.number.isRequired,
    dispatch: PropTypes.func.isRequired
};

const MealEditor = ({ dayId, meal, mealIndex, dispatch }) => {
    const { t } = useTranslation();

    const updateMeal = (field, value) => {
        dispatch({ type: 'updateMeal', dayId, mealId: meal._uiId, field, value });
    };

    const handleTypeChange = (event) => {
        const nextType = event.target.value;
        const oldDefaultTitle = meal.mealType
            ? t(`nutrition.mealsTypes.${meal.mealType}`, { defaultValue: '' })
            : '';
        updateMeal('mealType', nextType);
        if (!meal.title || meal.title === oldDefaultTitle) {
            updateMeal(
                'title',
                t(`nutrition.mealsTypes.${nextType}`, { defaultValue: nextType })
            );
        }
    };

    return (
        <Accordion defaultExpanded={mealIndex === 0} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ width: '100%', minWidth: 0 }}
                >
                    <RestaurantIcon color="primary" fontSize="small" />
                    <Typography fontWeight={600} noWrap sx={{ flex: 1 }}>
                        {meal.title ||
                            t('nutrition.editor.unnamedMeal', { number: mealIndex + 1 })}
                    </Typography>
                    <Chip
                        size="small"
                        label={t('nutrition.editor.optionsCount', {
                            count: meal.options.length
                        })}
                    />
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>{t('nutrition.mealType')}</InputLabel>
                            <Select
                                value={meal.mealType}
                                label={t('nutrition.mealType')}
                                onChange={handleTypeChange}
                            >
                                {meal.mealType && !mealTypes.includes(meal.mealType) && (
                                    <MenuItem value={meal.mealType}>
                                        {meal.mealType}
                                    </MenuItem>
                                )}
                                {mealTypes.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {t(`nutrition.mealsTypes.${type}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('nutrition.editor.mealTitle')}
                            value={meal.title}
                            onChange={(event) => updateMeal('title', event.target.value)}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ mt: 2 }} />
                {meal.options.map((option, optionIndex) => (
                    <Box key={option._uiId}>
                        <OptionEditor
                            dayId={dayId}
                            mealId={meal._uiId}
                            option={option}
                            optionIndex={optionIndex}
                            dispatch={dispatch}
                        />
                        {optionIndex < meal.options.length - 1 && <Divider />}
                    </Box>
                ))}

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mt: 1 }}
                >
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() =>
                            dispatch({
                                type: 'addOption',
                                dayId,
                                mealId: meal._uiId
                            })
                        }
                    >
                        {t('nutrition.addOption')}
                    </Button>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title={t('nutrition.editor.duplicateMeal')}>
                            <IconButton
                                aria-label={t('nutrition.editor.duplicateMeal')}
                                onClick={() =>
                                    dispatch({
                                        type: 'duplicateMeal',
                                        dayId,
                                        mealId: meal._uiId
                                    })
                                }
                            >
                                <ContentCopyIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('nutrition.editor.deleteMeal')}>
                            <IconButton
                                color="error"
                                aria-label={t('nutrition.editor.deleteMeal')}
                                onClick={() =>
                                    dispatch({
                                        type: 'deleteMeal',
                                        dayId,
                                        mealId: meal._uiId
                                    })
                                }
                            >
                                <DeleteOutlineIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};

MealEditor.propTypes = {
    dayId: PropTypes.string.isRequired,
    meal: PropTypes.object.isRequired,
    mealIndex: PropTypes.number.isRequired,
    dispatch: PropTypes.func.isRequired
};

const DaysEditorStep = ({ days, dispatch, requestedDayId, onDaySelected }) => {
    const { t } = useTranslation();
    const [selectedDayId, setSelectedDayId] = useState(requestedDayId || days[0]?._uiId);

    useEffect(() => {
        if (requestedDayId && days.some((day) => day._uiId === requestedDayId)) {
            setSelectedDayId(requestedDayId);
        }
    }, [days, requestedDayId]);

    useEffect(() => {
        if (!days.some((day) => day._uiId === selectedDayId)) {
            setSelectedDayId(days[0]?._uiId);
        }
    }, [days, selectedDayId]);

    const selectedDay = useMemo(
        () => days.find((day) => day._uiId === selectedDayId) || days[0],
        [days, selectedDayId]
    );

    const selectDay = (dayId) => {
        setSelectedDayId(dayId);
        onDaySelected(dayId);
    };

    if (!selectedDay) return null;

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
                <Typography variant="h5">{t('nutrition.editor.daysTitle')}</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {t('nutrition.editor.daysHelp')}
                </Typography>
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <List disablePadding sx={{ maxHeight: 520, overflowY: 'auto' }}>
                        {days.map((day) => (
                            <ListItemButton
                                key={day._uiId}
                                selected={day._uiId === selectedDay._uiId}
                                onClick={() => selectDay(day._uiId)}
                                divider
                            >
                                <ListItemText
                                    primary={t('nutrition.editor.dayNumber', {
                                        number: day.dayNumber
                                    })}
                                    secondary={t('nutrition.editor.mealsCount', {
                                        count: day.meals.length
                                    })}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>
            </Grid>

            <Grid item xs={12} md={9}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ sm: 'center' }}
                    spacing={1}
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography variant="h5">
                            {t('nutrition.editor.dayNumber', {
                                number: selectedDay.dayNumber
                            })}
                        </Typography>
                        <Typography color="text.secondary">
                            {t('nutrition.editor.dayHelp')}
                        </Typography>
                    </Box>
                    {Number(selectedDay.dayNumber) > 1 && (
                        <Button
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => {
                                if (
                                    selectedDay.meals.length === 0 ||
                                    window.confirm(
                                        t('nutrition.editor.replaceDayConfirm')
                                    )
                                ) {
                                    dispatch({
                                        type: 'copyPreviousDay',
                                        dayId: selectedDay._uiId
                                    });
                                }
                            }}
                        >
                            {t('nutrition.editor.copyPreviousDay')}
                        </Button>
                    )}
                </Stack>

                <TextField
                    fullWidth
                    label={t('nutrition.objective')}
                    value={selectedDay.objective}
                    onChange={(event) =>
                        dispatch({
                            type: 'updateDay',
                            dayId: selectedDay._uiId,
                            field: 'objective',
                            value: event.target.value
                        })
                    }
                    sx={{ mb: 2 }}
                />

                <Stack spacing={1.5}>
                    {selectedDay.meals.map((meal, mealIndex) => (
                        <MealEditor
                            key={meal._uiId}
                            dayId={selectedDay._uiId}
                            meal={meal}
                            mealIndex={mealIndex}
                            dispatch={dispatch}
                        />
                    ))}
                </Stack>

                {selectedDay.meals.length === 0 && (
                    <Paper
                        variant="outlined"
                        sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}
                    >
                        <RestaurantIcon sx={{ fontSize: 36, mb: 1 }} />
                        <Typography>{t('nutrition.editor.noMeals')}</Typography>
                    </Paper>
                )}

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{ mt: 2 }}
                    onClick={() =>
                        dispatch({ type: 'addMeal', dayId: selectedDay._uiId })
                    }
                >
                    {t('nutrition.addMeal')}
                </Button>
            </Grid>
        </Grid>
    );
};

DaysEditorStep.propTypes = {
    days: PropTypes.array.isRequired,
    dispatch: PropTypes.func.isRequired,
    requestedDayId: PropTypes.string,
    onDaySelected: PropTypes.func.isRequired
};

export default DaysEditorStep;
